import {
  type CreateDeploymentRequest,
  DEPLOYMENT_STATUS,
  type DeploymentDetailResponse,
  type DeploymentLookupByRunIdItem,
  type DeploymentResponse,
  type DeploymentsByRunIdsRequest,
  type DeploymentsByRunIdsResponse,
  type UpdateDeploymentRequest,
  type UpdateDeploymentResponse,
} from '@/shared/api/deploy/types';

type DeploymentPhase = 'deploy' | 'start' | 'stopped';

type MockDeploymentRecord = DeploymentDetailResponse & {
  run_id?: string | null;
  created_at: string;
  phase: DeploymentPhase;
  phaseStartedAt: number;
  server_type?: string | null;
  backend?: string | null;
  model_format?: string | null;
  convert_to_onnx?: boolean | null;
};

const DEPLOYMENTS_STORAGE_KEY = 'cyclone_demo_deployments';
const DEPLOYMENT_SUCCEEDED_MS = 2_000;
const SERVING_IN_PROGRESS_MS = 6_000;
const SERVING_ACTIVE_MS = 12_000;
const SERVING_RESTART_ACTIVE_MS = 6_000;

const loadDeployments = (): MockDeploymentRecord[] => {
  try {
    if (typeof localStorage === 'undefined') return [];
    const stored = localStorage.getItem(DEPLOYMENTS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('[deploymentMockStore] localStorage 배포 로드 실패:', error);
    return [];
  }
};

const saveDeployments = (deployments: MockDeploymentRecord[]): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(DEPLOYMENTS_STORAGE_KEY, JSON.stringify(deployments));
  } catch (error) {
    console.error('[deploymentMockStore] localStorage 배포 저장 실패:', error);
  }
};

let deployments = loadDeployments();

const getDeploymentEndpointName = (deployment: Pick<MockDeploymentRecord, 'deployment_name'>): string => {
  return deployment.deployment_name.replace(/[^a-zA-Z0-9-]/g, '-').toLowerCase();
};

const buildDeploymentEndpoints = (deployment: Pick<MockDeploymentRecord, 'deployment_name'>) => {
  const endpointName = getDeploymentEndpointName(deployment);

  return {
    http: `https://demo-serving.guardione.ai/v1/models/${endpointName}/infer`,
    grpc: `grpc://demo-serving.guardione.ai:443/${endpointName}`,
    metrics: `https://demo-serving.guardione.ai/metrics/${endpointName}`,
  };
};

const refreshDeployment = (deployment: MockDeploymentRecord, now: number = Date.now()): MockDeploymentRecord => {
  if (deployment.phase === 'stopped') {
    deployment.status = DEPLOYMENT_STATUS.SERVING_INACTIVE;
    deployment.replicas = 0;
    deployment.deleted_at = new Date(now).toISOString();
    deployment.updated_at = new Date(now).toISOString();
    return deployment;
  }

  const elapsed = now - deployment.phaseStartedAt;

  if (deployment.phase === 'start') {
    deployment.status =
      elapsed >= SERVING_RESTART_ACTIVE_MS ? DEPLOYMENT_STATUS.SERVING_ACTIVE : DEPLOYMENT_STATUS.SERVING_IN_PROGRESS;
  } else if (elapsed < DEPLOYMENT_SUCCEEDED_MS) {
    deployment.status = DEPLOYMENT_STATUS.DEPLOYMENT_IN_PROGRESS;
  } else if (elapsed < SERVING_IN_PROGRESS_MS) {
    deployment.status = DEPLOYMENT_STATUS.DEPLOYMENT_SUCCEEDED;
  } else if (elapsed < SERVING_ACTIVE_MS) {
    deployment.status = DEPLOYMENT_STATUS.SERVING_IN_PROGRESS;
  } else {
    deployment.status = DEPLOYMENT_STATUS.SERVING_ACTIVE;
  }

  deployment.replicas = Math.max(1, deployment.replicas || 1);
  deployment.deleted_at = null;
  deployment.updated_at = new Date(now).toISOString();

  if (deployment.status === DEPLOYMENT_STATUS.SERVING_ACTIVE) {
    deployment.deployed_at = deployment.deployed_at ?? new Date(now).toISOString();
    deployment.endpoints = buildDeploymentEndpoints(deployment);
    deployment.metadata = {
      ...deployment.metadata,
      endpoints: buildDeploymentEndpoints(deployment),
      hpa: deployment.metadata?.hpa ?? {
        enabled: false,
        min_replicas: null,
        max_replicas: null,
        target_cpu_utilization: null,
        target_memory_utilization: null,
      },
    };
  }

  return deployment;
};

const toLookupItem = (runId: string, deployment?: MockDeploymentRecord): DeploymentLookupByRunIdItem => {
  if (!deployment) {
    return {
      run_id: runId,
      found: false,
      deployment: null,
    };
  }

  const refreshed = refreshDeployment(deployment);

  return {
    run_id: runId,
    found: true,
    deployment: {
      deployment_id: refreshed.deployment_id,
      deployment_name: refreshed.deployment_name,
      model_name: refreshed.model_name || refreshed.deployment_name,
      model_version: refreshed.model_version || '1',
      run_id: refreshed.run_id ?? null,
      environment: refreshed.environment,
      namespace: refreshed.namespace || 'cyclone-demo',
      server_name: refreshed.server_name || `${getDeploymentEndpointName(refreshed)}-server`,
      server_type: refreshed.server_type || 'triton',
      backend: refreshed.backend ?? null,
      model_format: refreshed.model_format ?? null,
      convert_to_onnx: refreshed.convert_to_onnx ?? null,
      runtime_mode: refreshed.runtime_mode || 'cpu',
      status: refreshed.status,
      replicas: refreshed.replicas,
      gpu_count: 0,
      gpu_type: null,
      created_at: refreshed.created_at,
      updated_at: refreshed.updated_at || null,
      deployed_at: refreshed.deployed_at || null,
      deleted_at: refreshed.deleted_at || null,
    },
  };
};

export const createDeploymentMock = (request: CreateDeploymentRequest): DeploymentResponse => {
  const now = Date.now();
  const model = request.models[0];
  const deploymentId = `demo-deploy-${now}`;
  const deploymentName = request.deployment_name || `demo-deployment-${now}`;

  const deployment: MockDeploymentRecord = {
    deployment_id: deploymentId,
    deployment_name: deploymentName,
    model_name: model?.model_name || deploymentName,
    model_version: model?.model_version || '1',
    model_format: model?.model_format ?? null,
    environment: request.environment || 'dev',
    status: DEPLOYMENT_STATUS.DEPLOYMENT_IN_PROGRESS,
    replicas: request.replicas || 1,
    backend: request.backend ?? model?.model_format ?? null,
    convert_to_onnx: request.convert_to_onnx ?? null,
    deployed_at: null,
    deleted_at: null,
    updated_at: new Date(now).toISOString(),
    endpoints: buildDeploymentEndpoints({ deployment_name: deploymentName }),
    namespace: 'cyclone-demo',
    server_name: `${deploymentName}-server`,
    server_type: request.server_type,
    runtime_mode: request.runtime_mode,
    cpu: '2',
    memory: '4Gi',
    metadata: {
      endpoints: buildDeploymentEndpoints({ deployment_name: deploymentName }),
      hpa: {
        enabled: false,
        min_replicas: null,
        max_replicas: null,
        target_cpu_utilization: null,
        target_memory_utilization: null,
      },
    },
    run_id: request.run_id ?? null,
    created_at: new Date(now).toISOString(),
    phase: 'deploy',
    phaseStartedAt: now,
  };

  deployments = loadDeployments();
  deployments.push(deployment);
  saveDeployments(deployments);

  return {
    deployment_id: deploymentId,
    status: deployment.status,
    message: 'Demo deployment started',
    details: {
      replicas: deployment.replicas,
    },
  };
};

export const getDeploymentStatusMock = (deploymentId: string): DeploymentDetailResponse | null => {
  deployments = loadDeployments();
  const deployment = deployments.find((item) => item.deployment_id === deploymentId);
  if (!deployment) return null;

  const refreshed = refreshDeployment(deployment);
  saveDeployments(deployments);

  return { ...refreshed };
};

export const updateDeploymentMock = (
  deploymentId: string,
  request: UpdateDeploymentRequest
): UpdateDeploymentResponse | null => {
  deployments = loadDeployments();
  const deployment = deployments.find((item) => item.deployment_id === deploymentId);
  if (!deployment) return null;

  const now = Date.now();

  if (request.replicas === 0) {
    deployment.phase = 'stopped';
    deployment.phaseStartedAt = now;
    deployment.status = DEPLOYMENT_STATUS.SERVING_INACTIVE;
    deployment.replicas = 0;
    deployment.deleted_at = new Date(now).toISOString();
  } else if (request.replicas != null) {
    deployment.phase = 'start';
    deployment.phaseStartedAt = now;
    deployment.status = DEPLOYMENT_STATUS.SERVING_IN_PROGRESS;
    deployment.replicas = request.replicas;
    deployment.deleted_at = null;
  }

  deployment.updated_at = new Date(now).toISOString();
  saveDeployments(deployments);

  return {
    message: 'Demo deployment updated',
    deployment_id: deploymentId,
    revision: now,
    updated_fields: Object.keys(request),
    change_type: request.replicas === 0 ? 'scale_down' : 'scale_up',
    details: {
      replicas: deployment.replicas,
    },
  };
};

export const getDeploymentsByRunIdsMock = (request: DeploymentsByRunIdsRequest): DeploymentsByRunIdsResponse => {
  deployments = loadDeployments();

  const results = request.run_ids.map((runId) => {
    const deployment = deployments
      .filter((item) => String(item.run_id) === String(runId))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

    return toLookupItem(String(runId), deployment);
  });

  saveDeployments(deployments);

  return { results };
};

export const resetDeploymentMockStore = (): void => {
  deployments = [];

  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(DEPLOYMENTS_STORAGE_KEY);
    }
  } catch {
    // 테스트/비브라우저 환경에서 localStorage 접근 실패는 무시합니다.
  }
};
